import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Eye, Filter, ScanBarcode } from "lucide-react";
import {
  listAllGiftCardRedemption,
} from "../../../reducers/payouts.slice";
import { convertDateToCT, formatNumberToDollar, countActiveFiltersAndSort } from "../../../utils/util";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "../../../components/ui/dropdown-menu";
import StatusBadge from "../../../components/shared/StatusBadge";
import DataTable from "../../../components/shared/DataTable";
import PageHeader from "../../../components/shared/PageHeader";
import RefreshButton from "../../../components/shared/RefreshButton";
import SearchBar from "../../../components/shared/SearchBar";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const WITHDRAW_STATUS_LABEL = {
  PENDING_APPROVAL: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  READY_FOR_REDEMPTION: "Ready for Redemption",
};

const VendorWithdrawRequestPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getUsers();

  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("vendorWithdrawRequest_selectedStatuses");
    return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("vendorWithdrawRequest_sortDirection") || null;
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getPaymentDetails = () => {
    setLoading(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => {
        const createDate = convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm:ss A");
        const fullName = `${item?.user_details?.first_name} ${item?.user_details?.last_name}`;
        const description = item?.gift_card_details?.description;
        const actual_amount = formatNumberToDollar(item?.actual_amount);
        const paymentMethod = "Gift Card";
        const accountDetails = item?.payment_method_details?.account_number;
        return {
          ...item,
          createDate,
          fullName,
          actual_amount,
          description,
          paymentMethod,
          accountDetails,
        };
      });
      setLoading(false);
      setAllData(formatted || []);
    });
  };

  useEffect(() => {
    getPaymentDetails();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let result = [...allData];
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (item) =>
          item?.id?.toString().toLowerCase().includes(q) ||
          item?.fullName?.toLowerCase().includes(q)
      );
    }
    if (selectedStatuses.length > 0) {
      result = result.filter((item) => selectedStatuses.includes(item?.status));
    }
    if (sortDirection === "asc") {
      result.sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0));
    } else if (sortDirection === "desc") {
      result.sort((a, b) => Number(b?.amount || 0) - Number(a?.amount || 0));
    }
    setFilteredData(result);
    setCurrentPage(0);
  }, [allData, debouncedSearch, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("vendorWithdrawRequest_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("vendorWithdrawRequest_sortDirection", sortDirection);
    else sessionStorage.removeItem("vendorWithdrawRequest_sortDirection");
  }, [sortDirection]);

  const handleViewWithdrawRequest = (row) => {
    navigate("/dashboard/payouts/withdraw-request/view", {
      state: { requestDetail: row, type: "gift-card" },
    });
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "fullName", label: "Member" },
    { key: "paymentMethod", label: "Type" },
    { key: "description", label: "Description" },
    { key: "actual_amount", label: "Amount" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <StatusBadge status={row?.status} label={WITHDRAW_STATUS_LABEL[row?.status]} />
      ),
    },
    {
      key: "_action",
      label: "Action",
      render: (row) => (
        <div style={{ display: "flex", gap: 4 }}>
          {user?.role !== "MANAGER" && (
            <button
              type="button"
              aria-label="View Gift Card Details"
              onClick={(e) => { e.stopPropagation(); handleViewWithdrawRequest(row); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#c3c8cc",
                cursor: "pointer",
              }}
            >
              <Eye size={14} />
            </button>
          )}
          {row?.status === "APPROVED" && (
            <button
              type="button"
              aria-label="Gift Card Redemption"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/dashboard/payouts/withdraw-request/redemption", {
                  state: { requestDetail: row },
                });
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#c3c8cc",
                cursor: "pointer",
              }}
            >
              <ScanBarcode size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const pagedRows = filteredData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Wallet Withdraw Requests"
          subtitle="Keep track of all orders and wallet payout requests at a glance."
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 20,
        }}
      >
        {!isMobile && <RefreshButton onRefresh={getPaymentDetails} spinning={loading} />}

        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setDebouncedTerm={setDebouncedSearch}
          placeholder="Search..."
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Filter size={14} />
              Filter & Sort
              {activeFilterCount > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: "#00ADB5",
                    color: "#000",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "0 4px",
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            {["PENDING_APPROVAL", "APPROVED", "REJECTED", "READY_FOR_REDEMPTION"].map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={selectedStatuses.includes(status)}
                onCheckedChange={(checked) => handleStatusChange(status, checked)}
                onSelect={(e) => e.preventDefault()}
              >
                {WITHDRAW_STATUS_LABEL[status] || status.replace(/_/g, " ")}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
              <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
              Sort Amount (Low to High)
            </DropdownMenuItem>
            <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
              <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
              Sort Amount (High to Low)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DataTable
        columns={columns}
        rows={pagedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setCurrentPage(0); }}
        loading={loading}
      />
    </div>
  );
};

export default VendorWithdrawRequestPage;
